import { useEffect, useRef, useState } from 'react';
import {
  BookOpen,
  Users,
  Columns2,
  CircleDot,
  Heart,
  GitBranch,
  Smile,
  Link as LinkIcon,
  X,
  ChevronRight,
} from 'lucide-react';
import { StudioButton as Button } from './styles';
import type { Post } from '@/core/models/post';
import type { ComposerExtras } from './contracts';
import { AttachmentEditor } from './AttachmentEditor';
import { RingEditor } from './RingEditor';
import { PerspectiveEditor } from './PerspectiveEditor';
import { CollaborationEditor } from './CollaborationEditor';
import { TimelinePicker } from './TimelinePicker';
import { Panel, Grid, Row, Note, Field } from './styles';
import { safeSource } from './safeSource';
import { networks } from '@/features/networks/service';
const tools = [
  {
    key: 'context',
    label: 'Context card',
    description: 'Background and source links',
    Icon: BookOpen,
  },
  {
    key: 'collaboration',
    label: 'Collaborative draft',
    description: 'Write together in a private live draft',
    Icon: Users,
    network: true,
  },
  {
    key: 'perspective',
    label: 'Perspective split',
    description: 'Labelled and attributed viewpoints',
    Icon: Columns2,
    network: true,
  },
  {
    key: 'audience',
    label: 'Audience rings',
    description: 'Review a named group of recipients',
    Icon: CircleDot,
  },
  {
    key: 'cherishPrompt',
    label: 'Cherish prompt',
    description: 'Invite thoughtful replies',
    Icon: Heart,
  },
  {
    key: 'timeline',
    label: 'Timeline branch',
    description: 'Link a real follow-up update',
    Icon: GitBranch,
  },
  { key: 'mood', label: 'Mood', description: 'A public label, an emoji, or both', Icon: Smile },
] as const;
export type StudioTool = (typeof tools)[number]['key'] | 'link' | 'more' | 'location';
export function StudioTools({
  tool,
  setTool,
  owner,
  body,
  extras,
  onChange,
  onInsert,
  onThread,
  onGif,
}: {
  tool: StudioTool;
  setTool: (t: StudioTool | null) => void;
  owner: string;
  body: string;
  extras: ComposerExtras;
  onChange: (v: ComposerExtras) => void;
  onInsert: (text: string) => void;
  onThread: (p: Post) => void;
  onGif: () => void;
}) {
  const [network, setNetwork] = useState(false);
  const heading = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    let active = true;
    networks.capabilities().then(
      (r) => {
        if (active) setNetwork(!!r.flags.creator_collaboration);
      },
      () => {},
    );
    return () => {
      active = false;
    };
  }, []);
  useEffect(() => {
    heading.current?.focus();
  }, [tool]);
  const title =
    tool === 'more'
      ? 'More tools'
      : tool === 'link'
        ? 'Add link'
        : tool === 'location'
          ? 'Add location'
          : (tools.find((t) => t.key === tool)?.label ?? '');
  function done(next: ComposerExtras) {
    onChange(next);
    setTool(null);
  }
  return (
    <Panel
      aria-label={title}
      onKeyDown={(e) => {
        if (e.key === 'Escape') {
          e.stopPropagation();
          setTool(null);
        }
      }}
    >
      <Row style={{ justifyContent: 'space-between' }}>
        <h3 ref={heading} tabIndex={-1}>
          {title}
        </h3>
        <Button variant="ghost" aria-label="Close tools" onClick={() => setTool(null)}>
          <X size={18} />
        </Button>
      </Row>
      {tool === 'more' && (
        <>
          <Note>Go further with your ideas. Add only what helps your post.</Note>
          <Grid>
            {tools
              .filter((t) => !('network' in t) || network)
              .map((t) => (
                <Button
                  key={t.key}
                  onClick={() => setTool(t.key)}
                  style={{
                    justifyContent: 'flex-start',
                    textAlign: 'left',
                    padding: 14,
                    height: 'auto',
                  }}
                >
                  <t.Icon size={22} />
                  <span>
                    <strong>{t.label}</strong>
                    <Note as="span" style={{ display: 'block' }}>
                      {t.description}
                    </Note>
                  </span>
                  <ChevronRight size={16} />
                </Button>
              ))}
          </Grid>
          <Row>
            <Button
              onClick={() => {
                onGif();
                setTool(null);
              }}
            >
              Add GIF
            </Button>
            <Button
              onClick={() => {
                onInsert('@');
                setTool(null);
              }}
            >
              Mention people
            </Button>
            <Button onClick={() => setTool('location')}>Add location</Button>
          </Row>
        </>
      )}
      {(tool === 'context' || tool === 'cherishPrompt' || tool === 'mood') && (
        <AttachmentEditor
          key={tool}
          kind={tool}
          value={extras.attachments}
          onSave={(attachments, insert) => {
            if (insert) onInsert(insert);
            done({ ...extras, attachments });
          }}
        />
      )}
      {tool === 'audience' && <RingEditor onChoose={(ring) => done({ ...extras, ring })} />}
      {tool === 'perspective' && (
        <PerspectiveEditor
          userId={owner}
          onChoose={(perspective) => done({ ...extras, perspective })}
        />
      )}
      {tool === 'collaboration' && (
        <CollaborationEditor
          owner={owner}
          body={body}
          onChoose={(collaboration) => done({ ...extras, collaboration })}
        />
      )}
      {tool === 'timeline' && (
        <TimelinePicker
          owner={owner}
          onChoose={(post) => {
            onThread(post);
            setTool(null);
          }}
        />
      )}
      {tool === 'link' && (
        <LinkEditor
          onInsert={(text) => {
            onInsert(text);
            setTool(null);
          }}
        />
      )}
      {tool === 'location' && (
        <LocationEditor
          onInsert={(text) => {
            onInsert(text);
            setTool(null);
          }}
        />
      )}
    </Panel>
  );
}
function LocationEditor({ onInsert }: { onInsert: (text: string) => void }) {
  const [place, setPlace] = useState('');
  return (
    <>
      <Field>
        Place
        <input maxLength={120} value={place} onChange={(e) => setPlace(e.target.value)} />
      </Field>
      <Note>Appears as text in your post.</Note>
      <Button disabled={!place.trim()} onClick={() => onInsert(` (at ${place.trim()})`)}>
        Add place
      </Button>
    </>
  );
}
function LinkEditor({ onInsert }: { onInsert: (text: string) => void }) {
  const [url, setUrl] = useState('');
  return (
    <>
      <Note>Add a link to your text. YouTube and Twitch links can be shared here too.</Note>
      <Field>
        Link URL
        <input type="url" value={url} onChange={(e) => setUrl(e.target.value)} />
      </Field>
      <Button disabled={!safeSource(url)} onClick={() => onInsert(url)}>
        <LinkIcon size={16} />
        Insert link
      </Button>
    </>
  );
}
